import React from 'react';
import { Badge, Button, useToast } from '@parsina/framework';
import { Check, Copy } from 'lucide-react';

import { useTranslation, LTR_TEXT } from '../../i18n';
import { EMPTY } from '../../lib/format';
import { STATUS_TONE, leadStatusKey } from '../../constants/leadPipeline';
import ActorLabel from '../../components/ActorLabel';

/**
 * The small pieces the demo-request queue, drawer and dialogs share.
 * Composed from kit primitives; nothing here draws its own control.
 */

/** A catalogue label for a CRM vocabulary value; a value this build was never taught is echoed raw. */
export function useVocab() {
  const { t, has } = useTranslation();
  return React.useCallback((key, raw) => {
    if (raw == null || raw === '') return EMPTY;
    return key && has && has(key) ? t(key) : String(raw);
  }, [t, has]);
}

/** A lead status, as a badge. Colour never travels alone — the label is the badge. */
export function LeadStatusBadge({ status, 'data-testid': testId }) {
  const label = useVocab();
  if (!status) return <span>{EMPTY}</span>;
  const tone = STATUS_TONE[status] || 'outline';
  return (
    <Badge variant={tone} data-tone={tone} data-status={status} data-testid={testId}>
      {label(leadStatusKey(status), status)}
    </Badge>
  );
}

/** A machine identifier (reference, form id): LTR island, monospace. */
export function Code({ children, className = '', title, 'data-testid': testId }) {
  if (children == null || children === '') return <span>{EMPTY}</span>;
  return <span {...LTR_TEXT} className={`font-mono text-[12px] ${className}`} title={title} data-testid={testId}>{children}</span>;
}

/**
 * A reference in a tight spot (table cell, drawer header). The gateway's
 * reference is a 36-character request uuid; its first 8 characters are what a
 * rep reads out and matches against the visitor's confirmation screen, and the
 * full value stays one hover away — and in full, with a copy button, in the
 * drawer's «منشأ» section.
 */
export function ShortRef({ value, 'data-testid': testId }) {
  if (!value) return <Code data-testid={testId}>{value}</Code>;
  const short = value.length > 12 ? value.slice(0, 8) : value;
  return <Code title={value} data-testid={testId}>{short}</Code>;
}

/** One labelled fact in a drawer's definition list. */
export function Fact({ label, value, wide, 'data-testid': testId }) {
  return (
    <div className={`min-w-0 ${wide ? 'col-span-2' : ''}`} data-testid={testId}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="break-words font-semibold text-foreground">{value == null || value === '' ? EMPTY : value}</dd>
    </div>
  );
}

/**
 * Who owns a lead, as a person would say it: «شما» when it is the signed-in
 * rep, the truncated Keycloak subject otherwise (the CRM has no staff
 * directory in v1 — §13 Q4), «بدون مسئول» when nobody.
 */
export function OwnerLabel({ ownerId, mySub }) {
  const { t } = useTranslation();
  if (!ownerId) return <span className="text-muted-foreground">{t('demoRequests.owner.none')}</span>;
  if (mySub && ownerId === mySub) return <span className="font-semibold">{t('demoRequests.owner.you')}</span>;
  return <ActorLabel actor={ownerId} />;
}

/**
 * Copy a value to the clipboard. The toast says THAT it was copied, never
 * what — a phone number or e-mail does not belong in a toast (§13.2).
 */
export function CopyButton({ value, label, 'data-testid': testId }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [done, setDone] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setDone(true);
      toast.success(t('common.value.copied'));
      setTimeout(() => setDone(false), 1500);
    } catch {
      toast.error(t('demoRequests.drawer.copyFailed'));
    }
  };
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={copy}
      aria-label={label}
      title={label}
      data-testid={testId}
      className="h-8 w-8 p-0"
    >
      {done ? <Check size={15} aria-hidden /> : <Copy size={15} aria-hidden />}
    </Button>
  );
}

/** A drawer section: an h2 heading and its body. */
export function Section({
  title, action, children, 'data-testid': testId,
}) {
  return (
    <section className="flex flex-col gap-2.5" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-bold text-foreground">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}
